import { SearchOutlined } from '@ant-design/icons';
import { Flex, InputRef } from 'antd';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Table from 'components/Table';
import ColumnHeaderTooltip from '../../common/ColumnHeaderTooltip';
import TableSearchCustomDropdown from '../../common/TableSearchCustomDropdown';
import KPIRenderer from '../field-renderer/KPIFieldRenderer';
import SalesTargetPopover from '../SalesTargetPopover';
import PlantingGroupsPopover from './PlantingGroupsPopover';

import { KPI_PERCENTAGES_FOR_TARGETS } from 'screens/season-overview/constants';
import { selectToken } from 'store/auth/selectors';
import { Roles } from 'utils/constants';
import { isAuthorizedToAccessResource, renderInfoValue } from 'utils/helpers';

import type { SelectCommonPlacement } from 'antd/es/_util/motion';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import type {
  AgSeasonSummary,
  KPI,
  SeasonOverviewCommonWidgetFilters,
} from 'screens/season-overview/types';

interface AgTableProps extends SeasonOverviewCommonWidgetFilters {
  currency: string;
  data: AgSeasonSummary[];
  isLoading: boolean;
  pagination?: TableProps['pagination'];
  onRowClick?: (data: AgSeasonSummary) => void;
}

const AgronomistTable: React.FC<AgTableProps> = ({
  data,
  isLoading,
  currency,
  country,
  seasonTypeId,
  // pagination,
  onRowClick,
}: AgTableProps) => {
  const { t } = useTranslation();
  const token = useSelector(selectToken);

  const agronomistSearchInput = useRef<InputRef>(null);
  const srAgronomistSearchInput = useRef<InputRef>(null);

  const handleSearch = (
    _: string[],
    confirm: FilterDropdownProps['confirm'],
  ) => {
    confirm();
  };

  const handleReset = (clearFilters: FilterDropdownProps['clearFilters']) => {
    clearFilters?.();
  };

  const columns: ColumnsType<AgSeasonSummary> = useMemo(
    () =>
      [
        {
          title: t('seasonOverview.table.agronomist'),
          dataIndex: 'agronomistName',
          key: 'agronomist',
          onCell: (data: AgSeasonSummary) => ({
            onClick: () => onRowClick?.(data),
          }),
          className: 'clickable',
          showSorterTooltip: false,
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.agronomistName?.localeCompare(b?.agronomistName),
          render: (text: string) => (
            <Flex align="center" justify="space-between" gap={4}>
              <span>{renderInfoValue(text, 'N/A')}</span>
            </Flex>
          ),

          filterDropdown: (props: FilterDropdownProps) => (
            <TableSearchCustomDropdown
              {...props}
              ref={agronomistSearchInput}
              dataIndex="agronomistName"
              columnName={t('seasonOverview.table.agronomist')}
              onSearch={handleSearch}
              onReset={handleReset}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1677ff' : undefined }}
            />
          ),
          onFilter: (value: React.Key | boolean, record: AgSeasonSummary) =>
            record?.['agronomistName']
              ?.toLowerCase()
              ?.includes((value as string)?.toLowerCase()),
          filterDropdownProps: {
            onOpenChange(open: boolean) {
              if (open) {
                setTimeout(() => agronomistSearchInput.current?.select(), 100);
              }
            },
            placement: 'bottom' as SelectCommonPlacement,
          },
        },
        {
          title: t('seasonOverview.table.srAgronomist'),
          dataIndex: 'seniorAgronomistName',
          key: 'sragronomist',
          showSorterTooltip: false,
          className: 'clickable',
          render: (text: string) => renderInfoValue(text, 'N/A'),
          width: 200,
          enabled: isAuthorizedToAccessResource(token, [Roles.PM]),
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.seniorAgronomistName?.localeCompare(b?.seniorAgronomistName),
          filterDropdown: (props: FilterDropdownProps) => (
            <TableSearchCustomDropdown
              {...props}
              ref={srAgronomistSearchInput}
              dataIndex="seniorAgronomistName"
              columnName={t('seasonOverview.table.srAgronomist')}
              onSearch={handleSearch}
              onReset={handleReset}
            />
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1677ff' : undefined }}
            />
          ),
          onFilter: (value: React.Key | boolean, record: AgSeasonSummary) =>
            record?.['seniorAgronomistName']
              ?.toLowerCase()
              ?.includes((value as string)?.toLowerCase()),
          filterDropdownProps: {
            onOpenChange(open: boolean) {
              if (open) {
                setTimeout(
                  () => srAgronomistSearchInput?.current?.select(),
                  100,
                );
              }
            },
            placement: 'bottom' as SelectCommonPlacement,
          },
        },
        {
          title: t('seasonOverview.table.plantingGroups'),
          dataIndex: 'noOfGroups',
          key: 'plantingGroupCount',
          showSorterTooltip: false,
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.noOfGroups - b?.noOfGroups,
          render: (text: string, record: AgSeasonSummary) => (
            <PlantingGroupsPopover
              seasonTypeId={seasonTypeId}
              country={country}
              jrAgronomistId={record?.agronomistId}
            >
              {renderInfoValue((text || 0) + ' groups')}
            </PlantingGroupsPopover>
          ),
          className: 'clickable',
        },
        {
          title: () => (
            <ColumnHeaderTooltip
              title={t('seasonOverview.table.salesTargets')}
              tooltip={t('seasonOverview.tooltips.salesTarget', {
                ha: 200,
              })}
            />
          ),
          dataIndex: 'sales',
          key: 'salesTarget',
          showSorterTooltip: false,
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.sales?.actualTargetPercent - b?.sales?.actualTargetPercent,
          render: (salesTargetKPI: KPI) => (
            <SalesTargetPopover kpi={salesTargetKPI} currency={currency}>
              <KPIRenderer
                percentageLowerLimits={KPI_PERCENTAGES_FOR_TARGETS.salesTargets}
                data={salesTargetKPI}
                currency={currency}
              />
            </SalesTargetPopover>
          ),
        },
        {
          dataIndex: 'hectares',
          key: 'hectareTarget',
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.hectares?.actualTargetPercent - b?.hectares?.actualTargetPercent,
          showSorterTooltip: false,
          width: 200,
          title: () => (
            <ColumnHeaderTooltip
              title={t('seasonOverview.table.hectareTarget')}
              tooltip={t('seasonOverview.tooltips.hectareTarget', {
                ha: 200,
              })}
            />
          ),
          render: (hectareTargetKPI: KPI) => (
            <KPIRenderer
              percentageLowerLimits={KPI_PERCENTAGES_FOR_TARGETS.hectareTargets}
              data={hectareTargetKPI}
            />
          ),
        },
        {
          title: () => (
            <ColumnHeaderTooltip
              title={t('seasonOverview.table.deliveries')}
              tooltip={t('seasonOverview.tooltips.deliveries')}
            />
          ),
          showSorterTooltip: false,
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.deliveries?.actualTargetPercent -
            b?.deliveries?.actualTargetPercent,
          dataIndex: 'deliveries',
          key: 'deliveries',
          render: (deliveriesKPI: KPI) => (
            <KPIRenderer
              percentageLowerLimits={KPI_PERCENTAGES_FOR_TARGETS.deliveries}
              data={deliveriesKPI}
            />
          ),
        },
        {
          title: () => (
            <ColumnHeaderTooltip
              title={t('seasonOverview.table.seasonTasks')}
              tooltip={t('seasonOverview.tooltips.seasonTasks')}
            />
          ),
          showSorterTooltip: false,
          dataIndex: 'seasonTasks',
          key: 'seasonTasks',
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.seasonTasks?.actualTargetPercent -
            b?.seasonTasks?.actualTargetPercent,
          render: (seasonTasksKPI: KPI) => (
            <KPIRenderer
              percentageLowerLimits={KPI_PERCENTAGES_FOR_TARGETS.seasonTasks}
              data={seasonTasksKPI}
              denominatorKey="expectedTarget"
            />
          ),
        },
        {
          title: () => (
            <ColumnHeaderTooltip
              title={t('seasonOverview.table.collections')}
              tooltip={t('seasonOverview.tooltips.collections')}
            />
          ),
          showSorterTooltip: false,
          dataIndex: 'collection',
          key: 'collections',
          sorter: (a: AgSeasonSummary, b: AgSeasonSummary) =>
            a?.collection?.actualTargetPercent -
            b?.collection?.actualTargetPercent,
          render: (collectionsKPI: KPI) => (
            <KPIRenderer
              percentageLowerLimits={KPI_PERCENTAGES_FOR_TARGETS.collections}
              data={collectionsKPI}
              currency={currency}
            />
          ),
        },
      ].filter((item) => item?.enabled !== false),
    [t, country, seasonTypeId, currency, token],
  );

  return (
    <Table<AgSeasonSummary>
      loading={isLoading}
      tableHeader={columns}
      tableColumns={data}
      rowKey={'agronomistEmail'}
      // pagination={pagination}
      style={{ marginBottom: 20 }}
    />
  );
};

export default AgronomistTable;
